/**
 * Theme Token Definitions
 * Centralized theme configuration for consistent styling
 */
import { ThemeConfig } from '../types/theme'
export const themeTokens: ThemeConfig = {
  dark: {
    bg: 'bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900',
    card: 'bg-slate-800/50 backdrop-blur-xl border border-slate-700/50',
    text: 'text-white',
    shadow: 'shadow-lg shadow-purple-900/40',
    accent: 'from-purple-400 to-indigo-500',
  },
  light: {
    bg: 'bg-gradient-to-br from-white to-gray-100',
    card: 'bg-white/95 backdrop-blur-xl border border-gray-200/50',
    text: 'text-gray-900',
    shadow: 'shadow-lg shadow-gray-400/30',
    accent: 'from-violet-400 to-purple-500',
  },
  neon: {
    bg: 'bg-black',
    card: 'bg-black/90 backdrop-blur-xl border border-fuchsia-500/50',
    text: 'text-white',
    shadow: 'shadow-lg shadow-fuchsia-500/50',
    accent: 'from-cyan-400 to-fuchsia-500',
  },
  highContrast: {
    bg: 'bg-black',
    card: 'bg-white border-4 border-black',
    text: 'text-black',
    shadow: 'shadow-2xl shadow-black/50',
    accent: 'from-white to-gray-300',
  },
}
/**
 * Theme-aware utility classes
 * Use these instead of hardcoded colors
 */
export const getThemeAwareClasses = (theme: keyof typeof themeTokens) => ({
  // Text colors
  textPrimary:
    theme === 'light'
      ? 'text-gray-900'
      : theme === 'highContrast'
        ? 'text-black'
        : 'text-white',
  textSecondary:
    theme === 'light'
      ? 'text-gray-600'
      : theme === 'highContrast'
        ? 'text-gray-900'
        : theme === 'neon'
          ? 'text-cyan-300'
          : 'text-white/60',
  textTertiary:
    theme === 'light'
      ? 'text-gray-500'
      : theme === 'highContrast'
        ? 'text-gray-700'
        : theme === 'neon'
          ? 'text-fuchsia-300'
          : 'text-white/40',
  // Background overlays
  overlayLight:
    theme === 'light'
      ? 'bg-gray-100/80'
      : theme === 'highContrast'
        ? 'bg-gray-100'
        : theme === 'neon'
          ? 'bg-fuchsia-900/20'
          : 'bg-white/5',
  overlayMedium:
    theme === 'light'
      ? 'bg-gray-200/80'
      : theme === 'highContrast'
        ? 'bg-gray-200'
        : theme === 'neon'
          ? 'bg-fuchsia-900/30'
          : 'bg-white/10',
  overlayDark:
    theme === 'light'
      ? 'bg-gray-800/80'
      : theme === 'highContrast'
        ? 'bg-black'
        : theme === 'neon'
          ? 'bg-black/80'
          : 'bg-black/40',
  // Borders
  borderLight:
    theme === 'light'
      ? 'border-gray-200'
      : theme === 'highContrast'
        ? 'border-black'
        : theme === 'neon'
          ? 'border-cyan-500/30'
          : 'border-white/10',
  borderMedium:
    theme === 'light'
      ? 'border-gray-300'
      : theme === 'highContrast'
        ? 'border-black'
        : theme === 'neon'
          ? 'border-fuchsia-500/50'
          : 'border-white/20',
  // Interactive states
  hoverBg:
    theme === 'light'
      ? 'hover:bg-gray-100'
      : theme === 'highContrast'
        ? 'hover:bg-gray-200'
        : theme === 'neon'
          ? 'hover:bg-fuchsia-500/20'
          : 'hover:bg-white/10',
  activeBg:
    theme === 'light'
      ? 'bg-gray-200'
      : theme === 'highContrast'
        ? 'bg-gray-300'
        : theme === 'neon'
          ? 'bg-cyan-500/20'
          : 'bg-white/20',
})
/**
 * App-specific gradient colors
 * Semantic meaning per gradient:
 * - Blue/Indigo: Technology & depth
 * - Purple/Violet: Creativity & identity
 * - Pink/Rose: Emotion & storytelling
 * - Emerald/Teal: Trust & professionalism
 * - Amber/Orange: Humanity & contact
 * - Slate/Gray: Utility & settings
 */
export const appGradients = {
  home: 'from-violet-500 to-purple-500',
  portfolio: 'from-blue-500 to-indigo-500',
  expertise: 'from-purple-500 to-violet-500',
  process: 'from-pink-500 to-rose-500',
  about: 'from-emerald-500 to-teal-500',
  contact: 'from-amber-500 to-orange-500',
  settings: 'from-slate-500 to-gray-500',
}
/**
 * Project-specific gradient colors
 */
export const projectGradients = {
  aiVideo: 'from-rose-500 to-pink-600',
  apps: 'from-emerald-500 to-teal-600',
  ux: 'from-purple-500 to-violet-600',
  dashboard: 'from-blue-500 to-indigo-600',
}
/**
 * Neutral overlay colors for reducing visual chaos
 * Use between bright gradients for breathing room
 */
export const neutralOverlays = {
  dark: 'bg-slate-800/30',
  light: 'bg-gray-200/30',
  neon: 'bg-slate-900/50',
  highContrast: 'bg-gray-500/20',
}
/**
 * Depth hierarchy z-index values
 */
export const zIndexLayers = {
  dock: 10,
  windows: 100, // Base, increments from here
  overlays: 1000,
  modals: 2000,
  tooltips: 3000,
}
